import { useAction, useAtom } from '@reatom/npm-react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { Lock2Icon } from '@blade/ui-kit'
import {
  isAdminAtom,
  isLoggedInAtom,
  redirectToAuthEvent,
} from '@root/entities/auth'
import { Platform, platformAtom } from '@root/entities/platform'
import { PlatformSelector } from '@root/entities/platform/ui/desktop'
import { openCreateCurrencyModalEvent } from '@root/features/create-currency'
import { FunExploreRoute, WalletManagerRoute } from '@root/features/routing'
import {
  closeFavoriteDrawerEvent,
  openFavoriteDrawerEvent,
} from '@root/features/services/favorite-pair'
import { openSearchModalEvent } from '@root/features/services/search-pair'
import { settingScreenAtom } from '@root/features/settings/settings-screen/atoms'
import { ShortcutId } from '@root/features/shortcut/shortcut-id'
import { PossibleShortcut } from '@root/features/shortcut/ui/possible-shortcut'
import { StringUtil } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import {
  CandleChartOutlineIcon,
  ExploreIcon,
  NavigationGearOutlinedIcon,
  PlusInCircleExactIcon,
  ProfileIcon,
  SearchIcon,
  StarEmptyIcon,
  WalletOutlineIcon,
  WalletTrackerAlertIcon,
} from '@root/shared/ui-kit'
import { favoriteDrawerOpenAtom } from '@root/widgets/desktop/favorite-drawer'

import { navigationExpandedAtom } from '../atoms'

import { NavigationAccount } from './navigation-account'
import { NavigationItem } from './navigation-item'

import type { ClassNameProps } from '@root/shared/react'

type NavigationProps = {} & ClassNameProps

export const Navigation: React.FC<NavigationProps> = ({ className }) => {
  const { t } = useTranslation()
  const [expanded] = useAtom(navigationExpandedAtom)
  const [platform] = useAtom(platformAtom)
  const openSearchModal = useAction(openSearchModalEvent)
  const openCreateCurrencyModal = useAction(openCreateCurrencyModalEvent)
  const openFavoriteDrawer = useAction(openFavoriteDrawerEvent)
  const closeFavoriteDrawer = useAction(closeFavoriteDrawerEvent)
  const [favoriteDrawerOpen] = useAtom(favoriteDrawerOpenAtom)
  const [settingModalOpen, setSettingModalOpen] = useAtom(settingScreenAtom)

  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const [isAdmin] = useAtom(isAdminAtom)
  const redirectToAuth = useAction(redirectToAuthEvent)

  const handleFavoriteClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (favoriteDrawerOpen) {
        closeFavoriteDrawer()
        return
      }

      if (!isLoggedIn) {
        redirectToAuth()
        return
      }

      openFavoriteDrawer()
    },
    [
      closeFavoriteDrawer,
      favoriteDrawerOpen,
      isLoggedIn,
      openFavoriteDrawer,
      redirectToAuth,
    ],
  )

  const handleCreateCurrencyClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (!isLoggedIn) {
        redirectToAuth()
        return
      }

      openCreateCurrencyModal()
    },
    [isLoggedIn, openCreateCurrencyModal, redirectToAuth],
  )

  const navigationItems = useMemo(() => {
    const searchItem = (
      <PossibleShortcut key="search" shortcutId={ShortcutId.Search}>
        <NavigationItem
          onClick={openSearchModal}
          Icon={SearchIcon}
          tooltip={expanded ? undefined : t('common.search')}
        >
          {t('common.search')}
        </NavigationItem>
      </PossibleShortcut>
    )
    const ordersItem = (
      <NavigationItem
        key="orders"
        to="/orders"
        Icon={CandleChartOutlineIcon}
        authRequired={!isLoggedIn}
        tooltip={expanded ? undefined : t('order_other')}
      >
        {t('order_other')}
      </NavigationItem>
    )
    const profileItem = (
      <NavigationItem
        key="profile"
        to="/profile"
        Icon={ProfileIcon}
        authRequired={!isLoggedIn}
        tooltip={expanded ? undefined : t('common.profile')}
      >
        {t('common.profile')}
      </NavigationItem>
    )
    const walletTrackerItem = (
      <NavigationItem
        key="wallet-tracker"
        to="/wallet-tracker"
        Icon={WalletTrackerAlertIcon}
        authRequired={!isLoggedIn}
        tooltip={expanded ? undefined : t('navigation.wallet-tracker')}
      >
        {t('navigation.wallet-tracker')}
      </NavigationItem>
    )
    const walletManagerItem = (
      <NavigationItem
        key="wallet-manager"
        to={WalletManagerRoute.build()}
        Icon={WalletOutlineIcon}
        authRequired={!isLoggedIn}
        tooltip={
          expanded ? undefined : StringUtil.capitalize(t('wallet_other'))
        }
      >
        {StringUtil.capitalize(t('wallet_other'))}
      </NavigationItem>
    )
    const favoriteItem = (
      <NavigationItem
        key="favorite"
        active={favoriteDrawerOpen}
        onClick={handleFavoriteClick}
        Icon={StarEmptyIcon}
        tooltip={expanded ? undefined : t('navigation.favorite')}
      >
        {t('navigation.favorite')}
      </NavigationItem>
    )

    if (platform === Platform.Pump) {
      const exploreItem = (
        <NavigationItem
          key="explore"
          to={FunExploreRoute.build()}
          Icon={ExploreIcon}
          tooltip={expanded ? undefined : t('common.explore')}
        >
          {t('common.explore')}
        </NavigationItem>
      )

      const createCurrencyItem = (
        <NavigationItem
          key="create-currency"
          onClick={handleCreateCurrencyClick}
          Icon={PlusInCircleExactIcon}
          tooltip={expanded ? undefined : t('common.create')}
        >
          {t('common.create')}
        </NavigationItem>
      )

      return [
        exploreItem,
        searchItem,
        ordersItem,
        profileItem,
        walletTrackerItem,
        walletManagerItem,
        favoriteItem,
        createCurrencyItem,
      ]
    }

    const exploreItem = (
      <NavigationItem
        key="explore"
        to="/"
        Icon={ExploreIcon}
        tooltip={expanded ? undefined : t('common.explore')}
      >
        {t('common.explore')}
      </NavigationItem>
    )

    return [
      exploreItem,
      searchItem,
      ordersItem,
      profileItem,
      walletTrackerItem,
      walletManagerItem,
      favoriteItem,
    ]
  }, [
    expanded,
    favoriteDrawerOpen,
    handleCreateCurrencyClick,
    handleFavoriteClick,
    isLoggedIn,
    openSearchModal,
    platform,
    t,
  ])

  return (
    <Root className={className} aria-expanded={expanded}>
      <Top>{navigationItems}</Top>
      <PlatformSelector expanded={expanded} />
      <Bottom>
        {isAdmin ? (
          <NavigationItem
            to="/admin"
            Icon={Lock2Icon}
            authRequired={!isLoggedIn}
          >
            {t('back-office.navigation.title')}
          </NavigationItem>
        ) : undefined}
        <PossibleShortcut shortcutId={ShortcutId.Settings}>
          <NavigationItem
            active={!!settingModalOpen}
            onClick={() => setSettingModalOpen('main')}
            Icon={NavigationGearOutlinedIcon}
            tooltip={expanded ? undefined : t('settings')}
          >
            {t('settings')}
          </NavigationItem>
        </PossibleShortcut>
        <NavigationAccount />
      </Bottom>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr;
`

const Top = styled.div`
  width: 100%;
  padding-block-end: 10px;
  display: grid;
  gap: 6px;

  &::after {
    content: '';
    display: block;
    height: 1px;
    background: var(--background-3);

    margin-inline: 12px;
  }

  @container sidebar (height <= 700px) {
    gap: 0;
    padding-block-end: 6px;
  }
`

const Bottom = styled.div`
  width: 100%;
  margin-top: auto;
  display: grid;
  align-content: end;
`
